<template>
  <NcrmRecordEditDialog
    :model-value="modelValue"
    :title="$t('ncrm.contracts.details.dialogs.terminalBilling.title', { status: statusLabel(targetStatus) })"
    :subtitle="$t('ncrm.contracts.details.dialogs.terminalBilling.subtitle')"
    :loading="loading"
    :save-disabled="!canConfirm"
    :save-label="$t('ncrm.contracts.details.dialogs.terminalBilling.confirm')"
    header-icon="mdi-cash-clock"
    header-icon-color="warning"
    @update:model-value="$emit('update:modelValue', $event)"
    @save="$emit('confirm')"
  >
    <v-alert type="warning" density="comfortable" class="mb-4">
      {{ $t("ncrm.contracts.details.dialogs.terminalBilling.hint") }}
    </v-alert>

    <div class="ncrm-terminal-billing-dialog__summary mb-4">
      <div class="ncrm-terminal-billing-dialog__summary-row">
        <span>{{ $t("ncrm.contracts.details.dialogs.terminalBilling.summary.activePlans") }}</span>
        <strong>{{ formatNumber(resolvedImpact.active_billing_plan_count) }}</strong>
      </div>
      <div class="ncrm-terminal-billing-dialog__summary-row">
        <span>{{ $t("ncrm.contracts.details.dialogs.terminalBilling.summary.openPayments") }}</span>
        <strong>{{ formatNumber(resolvedImpact.open_payment_count) }}</strong>
      </div>
      <div class="ncrm-terminal-billing-dialog__summary-row">
        <span>{{ $t("ncrm.contracts.details.dialogs.terminalBilling.summary.openAmount") }}</span>
        <strong>{{ formatMoney(resolvedImpact.open_amount, currencyCode) }}</strong>
      </div>
      <div class="ncrm-terminal-billing-dialog__summary-row">
        <span>{{ $t("ncrm.contracts.details.dialogs.terminalBilling.summary.overdueAmount") }}</span>
        <strong>{{ formatMoney(resolvedImpact.overdue_amount, currencyCode) }}</strong>
      </div>
    </div>

    <v-radio-group
      :model-value="resolution"
      color="primary"
      hide-details
      @update:model-value="$emit('update:resolution', $event)"
    >
      <div class="ncrm-terminal-billing-dialog__option">
        <v-radio
          value="keep_receivables"
          :label="$t('ncrm.contracts.details.dialogs.terminalBilling.options.keep.title')"
        />
        <div class="ncrm-terminal-billing-dialog__option-hint">
          {{ $t("ncrm.contracts.details.dialogs.terminalBilling.options.keep.hint") }}
        </div>
      </div>
      <div class="ncrm-terminal-billing-dialog__option">
        <v-radio
          value="cancel_receivables"
          :label="$t('ncrm.contracts.details.dialogs.terminalBilling.options.cancel.title')"
        />
        <div class="ncrm-terminal-billing-dialog__option-hint">
          {{ $t("ncrm.contracts.details.dialogs.terminalBilling.options.cancel.hint") }}
        </div>
      </div>
    </v-radio-group>
  </NcrmRecordEditDialog>
</template>

<script>
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";

export default {
  name: "NcrmContractTerminalBillingDialog",
  components: {
    NcrmRecordEditDialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    targetStatus: {
      type: String,
      default: "",
    },
    resolution: {
      type: String,
      default: "keep_receivables",
    },
    impact: {
      type: Object,
      default: () => ({}),
    },
    currencyCode: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    statusLabel: {
      type: Function,
      default: (value) => String(value || "-"),
    },
    formatMoney: {
      type: Function,
      default: (value) => String(value ?? "0"),
    },
    formatNumber: {
      type: Function,
      default: (value) => String(value ?? 0),
    },
  },
  emits: ["update:modelValue", "update:resolution", "confirm"],
  computed: {
    resolvedImpact() {
      return {
        active_billing_plan_count: 0,
        open_payment_count: 0,
        open_amount: 0,
        overdue_amount: 0,
        ...(this.impact || {}),
      };
    },
    canConfirm() {
      return !this.loading && ["keep_receivables", "cancel_receivables"].includes(this.resolution);
    },
  },
};
</script>

<style scoped>
.ncrm-terminal-billing-dialog__summary {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  overflow: hidden;
}

.ncrm-terminal-billing-dialog__summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
}

.ncrm-terminal-billing-dialog__summary-row + .ncrm-terminal-billing-dialog__summary-row {
  border-top: 1px solid var(--ncrm-color-border-subtle);
}

.ncrm-terminal-billing-dialog__summary-row span {
  color: rgba(var(--v-theme-on-surface), 0.68);
  font-size: 0.88rem;
}

.ncrm-terminal-billing-dialog__summary-row strong {
  color: var(--ncrm-color-text-strong);
  font-weight: 700;
}

.ncrm-terminal-billing-dialog__option {
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  padding: 8px 12px 12px;
}

.ncrm-terminal-billing-dialog__option + .ncrm-terminal-billing-dialog__option {
  margin-top: 10px;
}

.ncrm-terminal-billing-dialog__option-hint {
  color: rgba(var(--v-theme-on-surface), 0.64);
  font-size: 0.86rem;
  line-height: 1.45;
  padding-left: 32px;
}
</style>
